import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ThumbsUp, Share2, Bookmark, MessageSquare, Headphones, Mail, Tag } from 'lucide-react';
import { Story } from '@/utils/storiesData';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import SubscribeDialog from '@/components/UI/SubscribeDialog';
import TagList from './TagList';
import SocialShareButtons from './SocialShareButtons';
import { toast } from '@/components/ui/use-toast';
interface RelatedStoryCardProps {
  story: Story;
}
const RelatedStoryCard: React.FC<RelatedStoryCardProps> = ({
  story
}) => {
  return <Link to={`/stories/${story.slug}`} className="block group">
      <div className="flex gap-3">
        <img src={story.coverImage} alt={story.title} className="w-16 h-16 object-cover" />
        <h4 className="font-medium text-sm group-hover:text-primary transition-colors line-clamp-2">
          {story.title}
        </h4>
      </div>
    </Link>;
};
interface StorySidebarProps {
  currentStory: Story;
  relatedStories: Story[];
}
const StorySidebar: React.FC<StorySidebarProps> = ({
  currentStory,
  relatedStories
}) => {
  const commentCount = currentStory.comments.length;
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const {
    resolvedTheme
  } = useTheme();
  const scrollToComments = () => {
    const commentsSection = document.getElementById('comments');
    if (commentsSection) {
      commentsSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: currentStory.title,
        text: currentStory.excerpt,
        url: window.location.href
      }).then(() => console.log('Successfully shared')).catch(error => console.error('Error sharing:', error));
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast({
          title: "Link copied to clipboard",
          description: "Share this link with others"
        });
      }).catch(() => {
        toast({
          title: "Unable to copy link",
          description: "Please try again or copy the URL manually",
          variant: "destructive"
        });
      });
    }
  };
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';
  const borderColor = resolvedTheme === 'dark' ? 'border-white/10' : 'border-gray-200';
  return <div className={cn("sidebar-sticky", isMobile ? "static" : "pl-4")}>
      
      {currentStory.tags && currentStory.tags.length > 0 && <div className={cn("mb-6 p-4 border rounded-md", borderColor)}>
          <div className="flex items-center gap-2 mb-3">
            <Tag size={18} className={textColor} />
            <h3 className={cn("text-sm font-medium", textColor)}>Related Topics</h3>
          </div>

          <TagList tags={currentStory.tags} variant="sidebar" />
        </div>}

      <div className={cn("mb-6 p-4 border rounded-md", borderColor)}>
        <SocialShareButtons url={window.location.href} title={currentStory.title} description={currentStory.excerpt} commentCount={commentCount} />
      </div>

      <div className={cn("mb-6 p-4 border rounded-md", borderColor)}>
        <div className="flex items-center gap-2 mb-3">
          <Headphones size={18} className={textColor} />
          <h3 className={cn("text-sm font-medium", textColor)}>Listen to Playlist</h3>
        </div>

        <p className={cn("text-xs mb-3", mutedTextColor)}>
          Curated music that complements this article's theme
        </p>

        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => window.open('/playlists', '_blank')}>
          Open Playlist
        </Button>
      </div>

      <div className={cn("p-4 border rounded-md", borderColor)}>
        <div className="flex items-center gap-2 mb-3">
          <Mail size={18} className={textColor} />
          <h3 className={cn("text-sm font-medium", textColor)}>Stay Updated</h3>
        </div>

        <p className={cn("text-xs mb-3", mutedTextColor)}>
          Subscribe to our newsletter for more stories like this
        </p>

        <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setSubscribeDialogOpen(true)}>
          Subscribe
        </Button>
      </div>

      <SubscribeDialog isOpen={subscribeDialogOpen} onClose={() => setSubscribeDialogOpen(false)} />
    </div>;
};
export default StorySidebar;